/**
 * Admin → Listings. The desk-review queue (submitted, oldest first) with a
 * status filter for everything else. Row click opens the review page.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Container, Link, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import adminService from '../services/adminService';
import DocumentCompletenessStrip from '../components/admin/DocumentCompletenessStrip';
import { panelSx, panelHeaderSx } from '../components/admin/surfaces';
import { STATUS_COPY, STATUS_CHIP_COLOR } from '../components/listings/ListingStatusBanner';
import { border, text } from '../theme/colors';

const FILTERS = ['submitted', 'changes_requested', 'desk_approved', 'verification_pending', 'verified', 'published', 'unpublished', 'sold', 'draft', 'rejected', 'withdrawn', 'all'];
// Each queue is a different job — say which one the admin is looking at.
const HEADINGS = {
  submitted: 'Waiting for desk review — oldest first',
  verification_pending: 'Verifier findings to sign off',
};
const PAGE_SIZE = 50;
// A queue can hold work from last year — the year is what tells a stale row from a fresh one.
const when = (v) => (v ? new Date(v).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function AdminListingsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'submitted';
  // Pagination lives in the URL so ?page=3 survives a reload and the back
  // button works. 1-based in the URL, 0-based for TablePagination.
  const parsedPage = parseInt(params.get('page'), 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setLoading(true); setError(''); const d = await adminService.listListings({ status, page: page + 1, limit: PAGE_SIZE }); setRows(d.listings); setTotal(d.total); }
    catch (e) { setError(e.response?.data?.error || 'Failed to load listings'); } finally { setLoading(false); }
  }, [status, page]);
  useEffect(() => { load(); }, [load]);

  // A filter change invalidates the offset: page 3 of the old set means nothing here.
  const setStatus = (next) => setParams({ status: next });
  const setPage = (next) => setParams(next > 0 ? { status, page: String(next + 1) } : { status });

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Property listings</Typography>
          <Typography variant="body2" sx={{ color: text.secondary }}>Desk review of realtor due-diligence dossiers. {total} in this view.</Typography>
        </Box>
        <Select size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
          {FILTERS.map((f) => <MenuItem key={f} value={f}>{f === 'all' ? 'All statuses' : (STATUS_COPY[f] || {}).title || f}</MenuItem>)}
        </Select>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>
      )}
      <Box sx={panelSx}>
        <Box sx={panelHeaderSx}><Typography variant="subtitle2">{HEADINGS[status] || 'Listings'}</Typography></Box>
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box> : (
          <>
            <Table size="small">
              <TableHead><TableRow><TableCell>Listing</TableCell><TableCell>Realtor</TableCell><TableCell>Dossier</TableCell><TableCell>Status</TableCell><TableCell>Submitted</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={5} sx={{ color: text.tertiary, py: 4, textAlign: 'center' }}>Nothing here.</TableCell></TableRow>}
                {rows.map((l) => (
                  <TableRow key={l.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/home/listings/${l.id}`)}>
                    {/* The title is a real link so the queue can be walked by keyboard; the row click is the convenience on top. */}
                    <TableCell><Link component={RouterLink} to={`/admin/home/listings/${l.id}`} onClick={(e) => e.stopPropagation()} underline="hover" sx={{ fontWeight: 600 }}>{l.title}</Link><Typography variant="caption" sx={{ display: 'block', color: text.secondary, textTransform: 'capitalize' }}>{l.listing_type}{l.location?.suburb ? ` · ${l.location.suburb}` : ''}{l.price_usd ? ` · USD ${Number(l.price_usd).toLocaleString()}` : ''}</Typography></TableCell>
                    <TableCell>{l.realtor?.display_name || '—'}<br /><Typography variant="caption" sx={{ color: text.secondary }}>{l.realtor?.agency_name || 'no agency'}</Typography></TableCell>
                    <TableCell><DocumentCompletenessStrip summary={{ ...l.dossier, items: [] }} noun="Items" /></TableCell>
                    <TableCell><Chip size="small" color={STATUS_CHIP_COLOR[l.status] || 'default'} label={(STATUS_COPY[l.status] || {}).title || l.status} /></TableCell>
                    <TableCell>{when(l.submitted_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              sx={{ borderTop: `1px solid ${border.light}` }}
            />
          </>
        )}
      </Box>
    </Container>
  );
}
